import { APIS } from '../../config/apis';
import { ALERT_TYPE, IDS, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { ServerRequest, ServerRequestAsync } from '../../utils/apiweb';
import { cloneEntityInit } from '../../utils/helpers';
import { getDateNow } from '../../utils/convert';

// DTO inicial del alta (fuente única: lo usan la vista de detalle y el alta directa de la grilla)
const VEHICULO_ENTITY_INIT = {
    vehiculo: {
        id: 0,
        idCuenta: 0,
        idEstadoCarga: 20,
        fechaCargaInicio: null,
        fechaCargaFin: null,
        dominio: '',
        dominioAnterior: '',
        anioModelo: '',
        marca: '',
        codigoMarca: '',
        modelo: '',
        idIncisoVehiculo: 0,
        idTipoVehiculo: 0,
        idCategoriaVehiculo: 0,
        numeroMotor: '',
        marcaMotor: '',
        numeroChasis: '',
        serieMotor: '',
        legajo: '',
        valuacion: 0,
        peso: '',
        carga: '',
        cilindrada: '',
        idOrigenFabricacion: 0,
        idCombustible: 0,
        idUsoVehiculo: 0,
        idMotivoBajaVehiculo: 0,
        recupero: '',
        radicacionAnterior: '',
        fechaAlta: null,
        fechaPatentamiento: null,
        fechaRadicacion: null,
        fechaTransferencia: null,
        fechaCompra: null,
        fechaBaja: null,
        fechaHabilitacionDesde: null,
        fechaHabilitacionHasta: null,
        fechaDDJJ: null,
        codigoMTM: ''
    },
    cuenta: {
        id: 0,
        numeroCuenta: '',
        numeroWeb: '',
        idEstadoCuenta: 1,
        idTipoTributo: 0,
        idTributo: 0,
        fechaAlta: null,
        fechaBaja: null
    },
    relacionesCuentas: [],
    archivos: [],
    observaciones: [],
    etiquetas: [],
    variablesCuenta: [],
    vinculosVehiculo: [],
    zonasEntrega: [],
    controladoresCuentas: [],
    condicionesEspeciales: [],
    recargosDescuentos: [],
    debitosAutomaticos: []
};

// Factory: cada consumidor recibe su propio clon, así nadie muta el DTO compartido
export const buildVehiculoEntityInit = () => cloneEntityInit(VEHICULO_ENTITY_INIT);

// Antigüedad mínima para tributar en el municipio: los vehículos de menos de 10 años están
// exceptuados (los cobra la provincia, no el municipio). El umbral es relativo al año actual.
export const ANIOS_ANTIGUEDAD_MINIMA = 10;

// ¿A este vehículo se le exige la antigüedad mínima? La excepción aplica a todos los vehículos
// salvo los "menores" (motos): esas las cobra el municipio sin importar el año. Es el único lugar
// donde vive el alcance de la regla; si mañana el negocio lo acota (p. ej. sólo ciertos incisos)
// se cambia sólo acá.
export const aplicaAntiguedadMinima = (gate) => !gate.vehiculoMenor;

// Valida el año/modelo del alta: devuelve el mensaje de error, o null si es válido.
// Mientras el año está incompleto (<4 dígitos) devuelve null para no marcar error al tipear.
// aplicaAntiguedad activa la excepción por antigüedad (ver aplicaAntiguedadMinima); el año inválido
// y el año futuro se validan siempre, para cualquier tipo de vehículo.
export const getAnioModeloError = (anioModelo, aplicaAntiguedad = false) => {
    if (!anioModelo || anioModelo.length < 4) return null;
    const anio = parseInt(anioModelo, 10);
    if (isNaN(anio) || anio < 1900) return 'Ingrese un año válido.';
    const anioActual = getDateNow().getFullYear();
    if (anio > anioActual) return 'El año no puede ser un año futuro.';
    if (aplicaAntiguedad && anio > anioActual - ANIOS_ANTIGUEDAD_MINIMA) {
        return `Los vehículos con menos de ${ANIOS_ANTIGUEDAD_MINIMA} años de antigüedad están exceptuados del pago de impuestos municipales.`;
    }
    return null;
};

// Gate del alta: inciso + tipo + año válido (con antigüedad mínima según el tipo de vehículo, ver
// getAnioModeloError/aplicaAntiguedadMinima) + dominio. Lo comparten la grilla y el selector de
// cuentas. A propósito es más estricto que el viejo form NEW (que admitía hasta el año siguiente):
// el alta no acepta vehículos no menores exceptuados por antigüedad.
export const isVehiculoGateValid = (gate) => {
    return gate.idIncisoVehiculo > 0 &&
        gate.idTipoVehiculo > 0 &&
        /^\d{4}$/.test(gate.anioModelo) &&
        getAnioModeloError(gate.anioModelo, aplicaAntiguedadMinima(gate)) === null &&
        (gate.vehiculoMenor ? DominioRegExps.MOTO : DominioRegExps.AUTO).test(gate.dominio);
};

export const UpdateEntityRow = (typeEntity, row, entity) => {
    // o:original a:add m:modify r:remove c:modify only childs

    if (typeEntity === 'RelacionCuenta') {
        if (row.state === 'a') {
            entity.relacionesCuentas.push(row);
        }
        else if (row.state === 'r') {
            if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                entity.relacionesCuentas = entity.relacionesCuentas.filter(f => f.id !== row.id);
            }
            else {
                let item = entity.relacionesCuentas.find(x => x.id === row.id);
                item.state = 'r';
            }
        }
    }

    if (typeEntity === 'VariableCuenta') {
        if (row.state === 'a') {
            entity.variablesCuenta.push(row);
        }
        else if (row.state === 'm') {
            const index = entity.variablesCuenta.indexOf(entity.variablesCuenta.find(x => x.id === row.id));
            if (index !== -1) {
                if (row.id < 0) row.state = 'a'; //originalmente era un alta
                entity.variablesCuenta[index] = row;
            }
        }
        else if (row.state === 'r') {
            if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                entity.variablesCuenta = entity.variablesCuenta.filter(f => f.id !== row.id);
            }
            else {
                let item = entity.variablesCuenta.find(x => x.id === row.id);
                item.state = 'r';
            }
        }
    }

    if (typeEntity === 'VinculoVehiculo') {
        if (row.state === 'a') {
            entity.vinculosVehiculo.push(row);
        }
        else if (row.state === 'm') {
            const index = entity.vinculosVehiculo.indexOf(entity.vinculosVehiculo.find(x => x.id === row.id));
            if (index !== -1) {
                if (row.id < 0) row.state = 'a'; //originalmente era un alta
                entity.vinculosVehiculo[index] = row;
            }
        }
        else if (row.state === 'r') {
            if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                entity.vinculosVehiculo = entity.vinculosVehiculo.filter(f => f.id !== row.id);
            }
            else {
                let item = entity.vinculosVehiculo.find(x => x.id === row.id);
                item.state = 'r';
            }
        }
    }

    if (typeEntity === 'ZonaEntrega') {
        if (row.state === 'a') {
            entity.zonasEntrega.push(row);
        }
        else if (row.state === 'm') {
            const index = entity.zonasEntrega.indexOf(entity.zonasEntrega.find(x => x.id === row.id));
            if (index !== -1) {
                if (row.id < 0) row.state = 'a'; //originalmente era un alta
                entity.zonasEntrega[index] = row;
            }
        }
        else if (row.state === 'r') {
            if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                entity.zonasEntrega = entity.zonasEntrega.filter(f => f.id !== row.id);
            }
            else {
                let item = entity.zonasEntrega.find(x => x.id === row.id);
                item.state = 'r';
            }
        }
    }

    if (typeEntity === 'ControladorCuenta') {
        if (row.state === 'a') {
            entity.controladoresCuentas.push(row);
        }
        else if (row.state === 'm') {
            const index = entity.controladoresCuentas.indexOf(entity.controladoresCuentas.find(x => x.id === row.id));
            if (index !== -1) {
                if (row.id < 0) row.state = 'a'; //originalmente era un alta
                entity.controladoresCuentas[index] = row;
            }
        }
        else if (row.state === 'r') {
            if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                entity.controladoresCuentas = entity.controladoresCuentas.filter(f => f.id !== row.id);
            }
            else {
                let item = entity.controladoresCuentas.find(x => x.id === row.id);
                item.state = 'r';
            }
        }
    }

    if (typeEntity === 'CondicionEspecial') {
        if (row.state === 'a') {
            entity.condicionesEspeciales.push(row);
        }
        else if (row.state === 'm') {
            const index = entity.condicionesEspeciales.indexOf(entity.condicionesEspeciales.find(x => x.id === row.id));
            if (index !== -1) {
                if (row.id < 0) row.state = 'a'; //originalmente era un alta
                entity.condicionesEspeciales[index] = row;
            }
        }
        else if (row.state === 'r') {
            if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                entity.condicionesEspeciales = entity.condicionesEspeciales.filter(f => f.id !== row.id);
            }
            else {
                let item = entity.condicionesEspeciales.find(x => x.id === row.id);
                item.state = 'r';
            }
        }
    }

    if (typeEntity === 'RecargoDescuento') {
        if (row.state === 'a') {
            entity.recargosDescuentos.push(row);
        }
        else if (row.state === 'm') {
            const index = entity.recargosDescuentos.indexOf(entity.recargosDescuentos.find(x => x.id === row.id));
            if (index !== -1) {
                if (row.id < 0) row.state = 'a'; //originalmente era un alta
                entity.recargosDescuentos[index] = row;
            }
        }
        else if (row.state === 'r') {
            if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                entity.recargosDescuentos = entity.recargosDescuentos.filter(f => f.id !== row.id);
            }
            else {
                let item = entity.recargosDescuentos.find(x => x.id === row.id);
                item.state = 'r';
            }
        }
    }

    if (typeEntity === 'DebitosAutomaticos') {
        if (row.state === 'a') {
            entity.debitosAutomaticos.push(row);
        }
        else if (row.state === 'm') {
            const index = entity.debitosAutomaticos.indexOf(entity.debitosAutomaticos.find(x => x.id === row.id));
            if (index !== -1) {
                if (row.id < 0) row.state = 'a'; //originalmente era un alta
                entity.debitosAutomaticos[index] = row;
            }
        }
        else if (row.state === 'r') {
            if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                entity.debitosAutomaticos = entity.debitosAutomaticos.filter(f => f.id !== row.id);
            }
            else {
                let item = entity.debitosAutomaticos.find(x => x.id === row.id);
                item.state = 'r';
            }
        }
    }

}

export const FindVehiculo = (state, setState, callbackNoSuccess, callbackError, callbackSuccessFindVehiculo) => {

    setState(prevState => {
        return { ...prevState, loading: true };
    });

    const paramsUrl = `/${state.id}`;

    ServerRequest(
        REQUEST_METHOD.GET,
        null,
        true,
        APIS.URLS.VEHICULO,
        paramsUrl,
        null,
        callbackSuccessFindVehiculo,
        callbackNoSuccess,
        callbackError
    );

}

export const ModifyVehiculo = (state, setState, dataTagger, vehiculo_formValues, cuenta_formValues, dispatch, dataTaggerActionClear, callbackNoSuccess, callbackError, callbackSuccessFindVehiculo) => {

    setState(prevState => {
        return { ...prevState, loading: true };
    });

    const callbackSuccess = (response) => {
        ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, "Vehículo actualizado correctamente", () => {
            dispatch(dataTaggerActionClear(state.processKey));
            callbackSuccessFindVehiculo(response);
        });
    };

    const dataBody = {
        vehiculo: {
            ...state.entity.vehiculo,
            idEstadoCarga: vehiculo_formValues.idEstadoCarga,
            dominio: vehiculo_formValues.dominio,
            dominioAnterior: vehiculo_formValues.dominioAnterior,
            anioModelo: vehiculo_formValues.anioModelo,
            marca: vehiculo_formValues.marca,
            codigoMarca: vehiculo_formValues.codigoMarca,
            modelo: vehiculo_formValues.modelo,
            idIncisoVehiculo: vehiculo_formValues.idIncisoVehiculo,
            idTipoVehiculo: vehiculo_formValues.idTipoVehiculo,
            idCategoriaVehiculo: vehiculo_formValues.idCategoriaVehiculo,
            numeroMotor: vehiculo_formValues.numeroMotor,
            marcaMotor: vehiculo_formValues.marcaMotor,
            numeroChasis: vehiculo_formValues.numeroChasis,
            serieMotor: vehiculo_formValues.serieMotor,
            legajo: vehiculo_formValues.legajo,
            valuacion: vehiculo_formValues.valuacion,
            peso: vehiculo_formValues.peso,
            carga: vehiculo_formValues.carga,
            cilindrada: vehiculo_formValues.cilindrada,
            idOrigenFabricacion: vehiculo_formValues.idOrigenFabricacion,
            idCombustible: vehiculo_formValues.idCombustible,
            idUsoVehiculo: vehiculo_formValues.idUsoVehiculo,
            idMotivoBajaVehiculo: vehiculo_formValues.idMotivoBajaVehiculo,
            recupero: vehiculo_formValues.recupero,
            radicacionAnterior: vehiculo_formValues.radicacionAnterior,
            fechaAlta: vehiculo_formValues.fechaAlta,
            fechaPatentamiento: vehiculo_formValues.fechaPatentamiento,
            fechaRadicacion: vehiculo_formValues.fechaRadicacion,
            fechaTransferencia: vehiculo_formValues.fechaTransferencia,
            fechaCompra: vehiculo_formValues.fechaCompra,
            fechaBaja: vehiculo_formValues.fechaBaja,
            fechaHabilitacionDesde: vehiculo_formValues.fechaHabilitacionDesde,
            fechaHabilitacionHasta: vehiculo_formValues.fechaHabilitacionHasta,
            fechaDDJJ: vehiculo_formValues.fechaDDJJ,
            codigoMTM: vehiculo_formValues.codigoMTM                                     
        },
        cuenta: {
            ...state.entity.cuenta,
            numeroCuenta: cuenta_formValues.numeroCuenta,
            numeroWeb: cuenta_formValues.numeroWeb
        },
        relacionesCuentas: state.entity.relacionesCuentas.filter(f => f.state !== 'o'),
        archivos: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Archivo.filter(f => f.state !== 'o') : [],
        observaciones: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Observacion.filter(f => f.state !== 'o') : [],
        etiquetas: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Etiqueta.filter(f => f.state !== 'o') : [],
        variablesCuenta: state.entity.variablesCuenta, //en este caso hacen falta todos para validar los rangos
        vinculosVehiculo: state.entity.vinculosVehiculo, //en este caso hacen falta todos para calcular el referente de la cuenta
        zonasEntrega: state.entity.zonasEntrega.filter(f => f.state !== 'o'),
        controladoresCuentas: state.entity.controladoresCuentas.filter(f => f.state !== 'o'),
        condicionesEspeciales: state.entity.condicionesEspeciales.filter(f => f.state !== 'o'),
        recargosDescuentos: state.entity.recargosDescuentos.filter(f => f.state !== 'o'),
        debitosAutomaticos: state.entity.debitosAutomaticos.filter(f => f.state !== 'o')
    };

    const paramsUrl = `/${state.id}`;

    ServerRequest(
        REQUEST_METHOD.PUT,
        null,
        true,
        APIS.URLS.VEHICULO,
        paramsUrl,
        dataBody,
        callbackSuccess,
        callbackNoSuccess,
        callbackError
    );

}

export const AddVehiculo = (state, setState, vehiculo_formValues, cuenta_formValues, dispatch, navigate, dataTaggerActionClear, callbackNoSuccess, callbackError) => {

    setState(prevState => {
        return { ...prevState, loading: true };
    });

    const callbackSuccess = (response) => {
        response.json()
            .then((row) => {
                ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, "Vehículo ingresado correctamente", () => {
                    dispatch(dataTaggerActionClear(state.processKey));
                    setState(prevState => {
                        return { ...prevState, loading: false };
                    });
                    navigate({
                        to: `/vehiculo/edit/${row.vehiculo.id}`,
                        refresh: true,
                        replace: true,
                    })
                });
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return { ...prevState, loading: false };
                });
            });
    };

    const dataBody = {
        vehiculo: {
            ...state.entity.vehiculo,
            idEstadoCarga: vehiculo_formValues.idEstadoCarga,
            dominio: vehiculo_formValues.dominio,
            dominioAnterior: vehiculo_formValues.dominioAnterior,
            anioModelo: vehiculo_formValues.anioModelo,
            marca: vehiculo_formValues.marca,
            codigoMarca: vehiculo_formValues.codigoMarca,
            modelo: vehiculo_formValues.modelo,
            idIncisoVehiculo: vehiculo_formValues.idIncisoVehiculo,
            idTipoVehiculo: vehiculo_formValues.idTipoVehiculo,
            idCategoriaVehiculo: vehiculo_formValues.idCategoriaVehiculo,
            numeroMotor: vehiculo_formValues.numeroMotor,
            marcaMotor: vehiculo_formValues.marcaMotor,
            numeroChasis: vehiculo_formValues.numeroChasis,
            serieMotor: vehiculo_formValues.serieMotor,
            legajo: vehiculo_formValues.legajo,
            valuacion: vehiculo_formValues.valuacion,
            peso: vehiculo_formValues.peso,
            carga: vehiculo_formValues.carga,
            cilindrada: vehiculo_formValues.cilindrada,
            idOrigenFabricacion: vehiculo_formValues.idOrigenFabricacion,
            idCombustible: vehiculo_formValues.idCombustible,
            idUsoVehiculo: vehiculo_formValues.idUsoVehiculo,
            idMotivoBajaVehiculo: vehiculo_formValues.idMotivoBajaVehiculo,
            recupero: vehiculo_formValues.recupero,
            radicacionAnterior: vehiculo_formValues.radicacionAnterior,
            fechaAlta: vehiculo_formValues.fechaAlta,
            fechaPatentamiento: vehiculo_formValues.fechaPatentamiento,
            fechaRadicacion: vehiculo_formValues.fechaRadicacion,
            fechaTransferencia: vehiculo_formValues.fechaTransferencia,
            fechaCompra: vehiculo_formValues.fechaCompra,
            fechaBaja: vehiculo_formValues.fechaBaja,
            fechaHabilitacionDesde: vehiculo_formValues.fechaHabilitacionDesde,
            fechaHabilitacionHasta: vehiculo_formValues.fechaHabilitacionHasta,
            fechaDDJJ: vehiculo_formValues.fechaDDJJ,
            codigoMTM: vehiculo_formValues.codigoMTM                                     
        },
        cuenta: {
            ...state.entity.cuenta,
            numeroCuenta: cuenta_formValues.numeroCuenta,
            numeroWeb: cuenta_formValues.numeroWeb
        },
        relacionesCuentas: [],
        archivos: [],
        observaciones: [],
        etiquetas: [],
        variablesCuenta: [],
        vinculosVehiculo: [],
        zonasEntrega: [],
        controladoresCuentas: [],
        condicionesEspeciales: [],
        recargosDescuentos: [],
        debitosAutomaticos: []
    };

    ServerRequest(
        REQUEST_METHOD.POST,
        null,
        true,
        APIS.URLS.VEHICULO,
        null,
        dataBody,
        callbackSuccess,
        callbackNoSuccess,
        callbackError
    );

}

export const ModifyBajaCuenta = (idCuenta, dataBody, state, setState, dispatch, navigate, dataTaggerActionClear, callbackNoSuccess, callbackError) => {

    setState(prevState => {
        return { ...prevState, loading: true };
    });

    const callbackSuccess = (response) => {
        response.json()
            .then((row) => {
                ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, "La cuenta fue dada de baja correctamente", () => {
                    dispatch(dataTaggerActionClear(state.processKey));
                    setState(prevState => {
                        return { ...prevState, loading: false };
                    });
                    navigate({
                        to: `/vehiculo/view/${row.idTributo}`,
                        refresh: true,
                        replace: true,
                    })
                });
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return { ...prevState, loading: false };
                });
            });
    };

    const paramsUrl = `/baja/${idCuenta}`;

    ServerRequest(
        REQUEST_METHOD.PUT,
        null,
        true,
        APIS.URLS.CUENTA,
        paramsUrl,
        dataBody,
        callbackSuccess,
        callbackNoSuccess,
        callbackError
    );

}

export const MakeListDomicilios = async (state) => {
    return new Promise(async (resolve, reject) => {
        try {
            let listDomicilios = [];

            if (state.entity.zonasEntrega.length > 0 && state.entity.cuenta.idDireccionEntrega) {
                let direccion = null;
                state.entity.zonasEntrega.forEach(x => {
                    if (x.direccion.id === state.entity.cuenta.idDireccionEntrega) {
                        direccion = x.direccion;
                    }
                });
                if (direccion) {
                    const domicilioEntrega = {...direccion, domicilio: "Entrega"};
                    listDomicilios.push(domicilioEntrega);
                }
            }

            const titular = state.entity.vinculosVehiculo.find(vinculo => vinculo.idTipoVinculoVehiculo === IDS.TIPO_VINCULO_VEHICULO.TITULAR)
            if (titular) {
                let direccion = null;

                const isPersonaFisica = (titular.idTipoPersona === IDS.TIPO_PERSONA.FISICA);
                const endpoint = isPersonaFisica ? APIS.URLS.PERSONA_FISICA : APIS.URLS.PERSONA_JURIDICA;
                const paramsUrl = `/${titular.idPersona}`;

                try {
                    const res = await ServerRequestAsync(REQUEST_METHOD.GET, null, true, endpoint, paramsUrl);
                    if (res.ok) {
                        try {
                            const data = await res.json();
                            direccion = isPersonaFisica ? data.direccion : data.domicilioLegal;
                        }
                        catch (error) {
                            reject(error);
                            return;
                        }
                    }
                    else {
                        reject(res);
                        return;
                    }
                }
                catch(error) {
                    reject(error);
                    return;
                }

                if (direccion) {
                    const domicilioTitular = {...direccion, domicilio: "Titular"};
                    listDomicilios.push(domicilioTitular);
                }
            }

            resolve(listDomicilios);
        }
        catch (error) {
            reject(error);
        }
    });
}


export function MakeListDomiciliosRelacionados(vinculosTributo) {
    return new Promise(async (resolve, reject) => {
        try {
            let listDomicilios = [];

            const promises = vinculosTributo.map(async (vinculo) => {
                if (vinculo.idTipoVinculoVehiculo === IDS.TIPO_VINCULO_VEHICULO.TITULAR || 
                    vinculo.idTipoVinculoVehiculo === IDS.TIPO_VINCULO_VEHICULO.RESPONSABLE_CUENTA) {
                    const isPersonaFisica = (vinculo.idTipoPersona === IDS.TIPO_PERSONA.FISICA);
                    const endpoint = isPersonaFisica ? APIS.URLS.PERSONA_FISICA : APIS.URLS.PERSONA_JURIDICA;
                    const paramsUrl = `/${vinculo.idPersona}`;

                    try {
                        const response = await ServerRequestAsync(REQUEST_METHOD.GET, null, true, endpoint, paramsUrl);
                        if (response.ok) {
                            const data = await response.json();
                            const direccion = isPersonaFisica ? data.direccion : data.domicilioLegal;
                            const tipoVinculo = vinculo.idTipoVinculoVehiculo === IDS.TIPO_VINCULO_VEHICULO.TITULAR ? 'Titular' : "Responsable de cuenta";
                            const domicilio = { ...direccion, tipoVinculo: tipoVinculo, nombrePersona: vinculo.nombrePersona };
                            return domicilio;
                        }
                    } catch (error) {
                        reject(error);
                    }
                }
            });

            const results = await Promise.all(promises);

            results.forEach(domicilio => {
                if (domicilio) {
                    listDomicilios.push(domicilio);
                }
            });

            resolve(listDomicilios);
        } catch (error) {
            reject(error);
        }
    });
  }


export const DominioRegExps = {
    AUTO: new RegExp("^(?:[A-Z]{3}\\d{3}|[A-Z]{2}\\d{3}[A-Z]{2})$"),
    MOTO: new RegExp("^(?:[A-Z]\\d{3}[A-Z]{3}|[A-Z]{3}\\d{3}|\\d{3}[A-Z]{3})$"),
    //MOTO: new RegExp('[A-z][0-9]{3}[A-z]{3}'),
}